import { SvgGrid, SvgList } from "@/assets/Svg";
import Image from "@/components/Image";
import { Skeleton } from "@/components/skeleton";
import { formatDisplay } from "@/utils/number-format";
import clsx from "clsx";
import throttle from "lodash.throttle";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"; // Imported React for React.memo
import { useShallow } from "zustand/shallow";
import SubscribeTrade, { SubscribePrice } from "../SubscribePrice";
import type { Pair as PairData } from "../store/pairs";
import { usePrice } from "../store/price";
import { useTradeStore } from "../store/trade-store";
import ChartView from "./chart";
import { usePriceHighlight } from "./hooks/useActiveBuySell";
import { useCheckClosePair } from "./hooks/useCheckClosePair";
import { useGetDataPair } from "./hooks/useDataPair";
import { useGetLeverage } from "./hooks/useGetLeverage";
import { useTradingCalc } from "./hooks/useTradingCalc";
import Tab from "./tab";

// --- Enums and Constants ---

export const MODE_GRID = "@defuture/mode_grid";

// --- Type Definitions (Inferred from usage) ---
export interface Pair {
  pairIndex: number;
  symbol: string;
  imageUrl: string;
  decimalsToShow: number;
  group: number; // Used for crypto filter (e.g., 0 for crypto)
}

interface PriceInfo {
  price?: string | number;
  price24h?: string | number;
}

interface PriceStoreData {
  [pairIndex: string]: PriceInfo | undefined;
}

// Assuming state structures for Zustand stores
export interface PairsState {
  data: PairData[];
}

interface PriceState {
  data: PriceStoreData;
}

// --- Child Components ---

interface PriceProps {
  decimalsToShow: number;
  price: string | number | undefined;
  pairIndex: number;
}

const Price = React.memo(function Price({
  decimalsToShow,
  price,
  pairIndex,
}: PriceProps) {
  const [throttledPrice, setThrottledPrice] = useState<number | null>(null);

  const { maxLeverage, initLeverage } = useGetLeverage(
    Number(pairIndex),
    false
  );

  const spreads = useTradeStore((state) => state.booking);

  const spread = useMemo(() => {
    return spreads.find((item) => item.pair_index === pairIndex)?.spread;
  }, [pairIndex, spreads]);

  const { sellPrice, buyPrice } = useTradingCalc({
    price: Number(throttledPrice),
    spread: Number(spread),
    volume: initLeverage,
    leverage: maxLeverage,
  });
  const throttledUpdate = useRef(
    throttle((newPrice: number) => {
      setThrottledPrice(newPrice);
    }, 1000)
  ).current;

  useEffect(() => {
    if (price != null) {
      throttledUpdate(Number(price));
    }
  }, [price, throttledUpdate]);

  const isActiveSell = usePriceHighlight(sellPrice, false);
  const isActiveBuy = usePriceHighlight(buyPrice, true);

  return (
    <div className="flex items-center gap-x-2 mt-4">
      <Skeleton loading={!throttledPrice}>
        <div
          className={clsx(
            "bg-transparentRed text-primary py-2 flex-1 rounded-lg font-sans font-medium text-xs transition-colors",
            {
              "!bg-[#FF695F]": isActiveSell,
            }
          )}
        >
          Sell
          <p>
            {formatDisplay(Number(sellPrice) || 0, {
              decimalToShow: decimalsToShow,
              minimumDecimalToShow: decimalsToShow,
            })}
          </p>
        </div>

        <div
          className={clsx(
            "bg-transparentGreen text-primary py-2 flex-1 font-sans font-medium rounded-lg text-xs transition-colors",
            {
              "!bg-[#38F24A]": isActiveBuy,
            }
          )}
        >
          Buy
          <p>
            {formatDisplay(Number(buyPrice) || 0, {
              decimalToShow: decimalsToShow,
              minimumDecimalToShow: decimalsToShow,
            })}
          </p>
        </div>
      </Skeleton>
    </div>
  );
});

interface Price24hProps {
  rawPrice24h: number | string | undefined;
}

const Price24h = React.memo(function Price24h({ rawPrice24h }: Price24hProps) {
  const [throttledPrice24h, setThrottledPrice24h] = useState<number | null>(
    null
  );
  const throttledUpdate = useRef(
    throttle((newPrice: number) => {
      setThrottledPrice24h(newPrice);
    }, 1000)
  ).current;

  useEffect(() => {
    if (rawPrice24h != null) {
      throttledUpdate(Number(rawPrice24h));
    }
  }, [rawPrice24h, throttledUpdate]);

  const prefix = throttledPrice24h != null && throttledPrice24h >= 0 ? "+" : "";
  const displayData = useMemo(() => {
    if (!throttledPrice24h || Math.abs(throttledPrice24h) < 0.001) {
      return 0;
    }
    return formatDisplay(throttledPrice24h, {
      decimalToShow: Math.abs(throttledPrice24h) > 0.01 ? 2 : 3,
    });
  }, [throttledPrice24h]);

  const textColor =
    throttledPrice24h != null && throttledPrice24h >= 0 ? "#38F24A" : "#FF695F";

  return (
    <p style={{ color: textColor }} className="text-sm font-sans font-medium">
      {throttledPrice24h != null ? `${prefix}${displayData}%` : "--"}
    </p>
  );
});

// --- Main Market Component ---

function ItemGrid({
  item,
  rawPrice24h,
  price,
}: {
  item: PairData;
  rawPrice24h: number | string | undefined;
  price: number | string | undefined;
}) {
  const { isClose } = useCheckClosePair(item.pairIndex);

  return (
    <button
      type="button"
      key={item.pairIndex} // Use a stable, unique key
      onClick={() => {
        window.parent.postMessage(
          {
            type: "OPEN_MINI_APP",
            url: `https://world.org/mini-app?app_id=app_60c094705c114bff41be480e736d9482&path=${encodeURIComponent(
              `/?pairIndex=${item.pairIndex}`
            )}`,
          },
          "*"
        );
      }}
      className={clsx(
        "flex flex-col justify-between border border-border1",
        "p-4 bg-card rounded-2xl min-h-[128px]",
        "transition-colors" // Added a subtle hover effect for items
      )}
    >
      <div className="flex items-center  gap-2">
        <div className="w-5 h-5 rounded-full flex-shrink-0">
          <Image
            src={item.imageUrl}
            alt={`${item.symbol} logo`} // Improved alt text
            width={20}
            height={20}
            loading="lazy"
          />
        </div>
        <div className="flex items-center gap-x-1 mr-auto">
          <p className="text-base text-primary font-medium flex-1 truncate">
            {item.nameDisplay}
          </p>
        </div>
        {!isClose && <Price24h rawPrice24h={rawPrice24h} />}
        {isClose && <p className="text-[#FF695F] text-sm">CLOSE</p>}
      </div>

      <div className="w-full pointer-events-none">
        <ChartView
          symbol={item.symbol}
          historyCall={item.historyCall ?? ""}
          rawPrice24h={rawPrice24h}
        />
      </div>
      <div className="flex items-center justify-between gap-x-2">
        <div className="flex-1">
          <Price
            price={price}
            decimalsToShow={item.decimalsToShow}
            pairIndex={item.pairIndex}
          />
        </div>
      </div>
    </button>
  );
}

function ItemList({
  item,
  rawPrice24h,
  price,
}: {
  item: PairData;
  rawPrice24h: number | string | undefined;
  price: number | string | undefined;
}) {
  const { isClose } = useCheckClosePair(item.pairIndex);

  return (
    <button
      type="button"
      key={item.pairIndex} // Use a stable, unique key
      onClick={() => {
        window.parent.postMessage(
          {
            type: "OPEN_MINI_APP",
            url: `https://world.org/mini-app?app_id=app_60c094705c114bff41be480e736d9482&path=${encodeURIComponent(
              `/?pairIndex=${item.pairIndex}`
            )}`,
          },
          "*"
        );
      }}
      className={clsx(
        "flex flex-col justify-between border border-border1",
        "p-4 bg-card rounded-2xl min-h-[128px]",
        "transition-colors" // Added a subtle hover effect for items
      )}
    >
      <div className="flex items-center  gap-2">
        <div className="w-5 h-5 rounded-full ">
          <Image
            src={item.imageUrl}
            alt={`${item.symbol} logo`} // Improved alt text
            width={20}
            height={20}
            loading="lazy"
          />
        </div>
        <div className="flex items-center gap-x-1 font-sans font-medium">
          <p className="text-base text-primary font-medium flex-1 truncate">
            {item.nameDisplay}
          </p>
        </div>
        <Price24h rawPrice24h={rawPrice24h} />
        {isClose && <p className="text-[#FF695F] text-sm">CLOSE</p>}
      </div>

      <div className="flex items-center gap-x-2">
        <div className="w-full basis-[50%]">
          <ChartView
            symbol={item.symbol}
            historyCall={item.historyCall ?? ""}
            rawPrice24h={rawPrice24h}
          />
        </div>
        <div className="flex items-center justify-between gap-x-2 flex-1">
          <div className="flex-1">
            <Price
              price={price}
              decimalsToShow={item.decimalsToShow}
              pairIndex={item.pairIndex}
            />
          </div>
        </div>
      </div>
    </button>
  );
}

function MarketUI({
  filteredPairs,
  typeList,
}: {
  filteredPairs: PairData[];
  typeList: boolean;
}) {
  const prices = usePrice(useShallow((state: PriceState) => state.data));

  return (
    <div
      className={clsx("grid grid-cols-2 gap-1", {
        "!grid-cols-1": typeList,
      })}
    >
      {filteredPairs
        .filter((pair) => !pair.isHidden)
        .map((item) => {
          const currentPriceInfo = prices[String(item.pairIndex)];
          const price = currentPriceInfo?.price;
          const rawPrice24h = currentPriceInfo?.price24h;

          if (typeList) {
            return (
              <ItemList
                key={item.pairIndex}
                item={item}
                price={price}
                rawPrice24h={rawPrice24h}
              />
            );
          }

          return (
            <ItemGrid
              key={item.pairIndex}
              item={item}
              price={price}
              rawPrice24h={rawPrice24h}
            />
          );
        })}
    </div>
  );
}

export function Market() {
  const { tab, onChangeTab, formatted } = useGetDataPair();
  const [typeList, setTypeList] = useState(false);
  const [render, setRender] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(MODE_GRID) === "true";
    setTypeList(saved);
    setRender(true);
  }, []);

  const handleToggleGrid = useCallback(() => {
    setTypeList((prev) => {
      localStorage.setItem(MODE_GRID, String(!prev));
      return !prev;
    });
  }, []);

  if (!render) {
    return null;
  }

  return (
    <div className="bg-background no-scrollbar min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <Tab tabActive={tab} onChangeTab={onChangeTab} />

        <div className="flex justify-end">
          <button type="button" onClick={handleToggleGrid}>
            {typeList ? <SvgGrid /> : <SvgList />}
          </button>
        </div>
      </div>
      <MarketUI filteredPairs={formatted} typeList={typeList} />
      <SubscribePrice />
      <SubscribeTrade />
    </div>
  );
}
